import DateFnsUtils from "@date-io/date-fns";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    ListItem,
    ListItemIcon,
    ListItemText,
    MenuItem,
    TextField,
    Tooltip,
} from "@material-ui/core";
import { red } from "@material-ui/core/colors";
import { ThemeProvider, createMuiTheme } from "@material-ui/core/styles";
import { Info } from "@material-ui/icons";
import { DateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import * as Icons from "@mui/icons-material/";
import enLocale from "date-fns/locale/en-US";
import esLocale from "date-fns/locale/es";
import { useSnackbar } from "notistack";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { AutoSizer, CellMeasurer, CellMeasurerCache, List } from "react-virtualized";
import i18n from "../../../../../../i18n";
import { MarkAsReadCommand, UserNotificationDto } from "../../../../../api/APIClient";
import { notificationsService } from "../../../../../api/Services";
import { usePanelProjectContext } from "../../../../PanelProjectProvider";
import "./styles.css";

const defaultMaterialTheme = createMuiTheme({
    palette: {
        primary: red,
    },
});

const localeMap = {
    en: enLocale,
    es: esLocale,
    fr: enLocale,
};

interface IHistoricalAdvertises {
    open: boolean;
    close: () => void;
    information: UserNotificationDto[];
    type: string;
    setAdvertise: React.Dispatch<React.SetStateAction<UserNotificationDto[]>>;
    setAdvertiseCount: React.Dispatch<React.SetStateAction<number>>;
}

interface IHistoricalInformation {
    title: string;
    iconAvailable: JSX.Element;
    iconUnavailable: JSX.Element;
    data: {
        title: string;
        description: string;
        date: string;
        read: boolean;
        created: Date;
        ids: string;
        values?: string[];
    }[];
}

const HistoricalAdvertises = ({
    open,
    close,
    information,
    type,
    setAdvertise,
    setAdvertiseCount,
}: IHistoricalAdvertises) => {
    const { t } = useTranslation("project");
    const { project } = usePanelProjectContext();

    const { enqueueSnackbar } = useSnackbar();
    const [locale, setLocale] = React.useState<string>("es");

    const [loading, setLoading] = React.useState<boolean>(true);
    const [historicalInformation, setHistoricalInformation] =
        React.useState<IHistoricalInformation>();
    const [filteredHistoricalInformation, setFilteredHistoricalInformation] =
        React.useState<IHistoricalInformation>();

    const [startDate, setStartDate] = React.useState<Date | undefined>();
    const [endDate, setEndDate] = React.useState<Date | undefined>();
    const [status, setStatus] = React.useState<string>("all");

    const handleHistoricalInformation = async (content: UserNotificationDto[]) => {
        switch (type) {
            case "notifications":
                setHistoricalInformation({
                    title: "dashboard.cards.notifications",
                    iconAvailable: React.createElement(Icons.MarkEmailUnread),
                    iconUnavailable: React.createElement(Icons.MarkEmailRead, {
                        color: "disabled",
                    }),
                    data: content.map((not: UserNotificationDto) => {
                        return {
                            title: `${not.title}`,
                            description: `${not.description}`,
                            date: not.created.toString(),
                            read: not.read,
                            created: not.created,
                            ids: not.ids,
                        };
                    }),
                });
                setLoading(false);
                break;
            default:
                setLoading(false);
                break;
        }
    };

    const handleReadAdvertise = async (ids: string, projectId: string) => {
        if (!ids) return;

        const newNotId = ids.split(",").map((x) => Number(x));
        const command: MarkAsReadCommand = { ids: newNotId, projectId };

        switch (type) {
            case "notifications":
                await notificationsService.markAsRead(command);
                notificationsService.getUnreadNotifications(projectId).then((res) => {
                    if (res) {
                        setAdvertise(res.data);
                        setAdvertiseCount(res.data.length);
                    } else {
                        setAdvertise([]);
                        setAdvertiseCount(0);
                    }
                });
                break;
            default:
                break;
        }
    };

    const getAdvertiseHistoryList = async () => {
        switch (type) {
            case "notifications":
                notificationsService
                    .getAllNotificationsByUser(project.id)
                    .then((res) => {
                        handleHistoricalInformation(res.data);
                    })
                    .catch((err) => {
                        enqueueSnackbar(`${err.errorDescription.message}`, {
                            variant: "error",
                            anchorOrigin: {
                                horizontal: "right",
                                vertical: "bottom",
                            },
                        });
                        console.error(err);
                    });
                break;
            default:
                break;
        }
    };

    const convertDateIntoString = (date: Date | undefined) => {
        if (!date) return "";
        const newDate = new Date(date);
        return newDate.toLocaleString();
    };

    const handleChangeStauts = (event: React.ChangeEvent<HTMLInputElement>) => {
        setStatus(event.target.value);
    };

    const closeDialog = () => {
        setLoading(true);
        setHistoricalInformation(undefined);
        setFilteredHistoricalInformation(undefined);
        setStartDate(undefined);
        setEndDate(undefined);
        setStatus("all");
        close();
    };

    React.useEffect(() => {
        setLocale(i18n.language);
        if (information) handleHistoricalInformation(information);
    }, [open, information]);

    React.useEffect(() => {
        if (!historicalInformation) return;
        const filteredData = historicalInformation.data.filter((x) => {
            const date = new Date(x.created);
            return date >= (startDate ?? new Date(0)) && date <= (endDate ?? new Date(Date.now()));
        });
        switch (status) {
            case "read":
                setFilteredHistoricalInformation({
                    ...historicalInformation,
                    data: filteredData.filter((x) => x.read),
                });
                break;
            case "unread":
                setFilteredHistoricalInformation({
                    ...historicalInformation,
                    data: filteredData.filter((x) => !x.read),
                });
                break;
            default:
                setFilteredHistoricalInformation({
                    ...historicalInformation,
                    data: filteredData,
                });
                break;
        }
    }, [historicalInformation, startDate, endDate, status]);

    const cache = React.useRef(
        new CellMeasurerCache({
            fixedWidth: true,
            defaultHeight: 100,
        })
    );

    const [heightCalc, setHeightCalc] = React.useState<number>(window.innerHeight / 2);

    React.useEffect(() => {
        setHeightCalc(window.innerHeight / 2);
        cache.current.clearAll();
    }, [window.innerHeight, window.innerWidth]);

    return (
        <Dialog open={open} onClose={closeDialog} maxWidth="md" fullWidth>
            <DialogTitle className="dialogTitle">
                {t(`${historicalInformation?.title}`)}
            </DialogTitle>
            <DialogContent
                style={{
                    alignItems: "self-end",
                }}
            >
                <Grid container>
                    <Grid item xs={6}>
                        <FormControl
                            style={{
                                width: "70%",
                            }}
                        >
                            <TextField
                                id="outlined-select-signals"
                                select
                                label={t("dashboard.cards.show")}
                                value={status}
                                onChange={handleChangeStauts}
                            >
                                <MenuItem key="all" value="all">
                                    {t("dashboard.cards.all")}
                                </MenuItem>
                                <MenuItem key="read" value="read">
                                    {t("dashboard.cards.read")}
                                </MenuItem>
                                <MenuItem key="unread" value="unread">
                                    {t("dashboard.cards.unread")}
                                </MenuItem>
                            </TextField>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <ThemeProvider theme={defaultMaterialTheme}>
                            <MuiPickersUtilsProvider
                                utils={DateFnsUtils}
                                locale={localeMap[locale]}
                            >
                                <DateTimePicker
                                    label={t("dashboard.cards.startDate")}
                                    variant="dialog"
                                    ampm={false}
                                    disableFuture={true}
                                    value={
                                        startDate ??
                                        new Date(Date.now()).setDate(
                                            new Date(Date.now()).getDate() - 3
                                        )
                                    }
                                    onChange={(e) => setStartDate(new Date(`${e?.toUTCString()}`))}
                                    onError={console.log}
                                    format="yyyy/MM/dd HH:mm"
                                />
                            </MuiPickersUtilsProvider>
                        </ThemeProvider>
                        <ThemeProvider theme={defaultMaterialTheme}>
                            <MuiPickersUtilsProvider
                                utils={DateFnsUtils}
                                locale={localeMap[locale]}
                            >
                                <DateTimePicker
                                    label={t("dashboard.cards.endDate")}
                                    variant="dialog"
                                    ampm={false}
                                    disableFuture={true}
                                    value={endDate ?? new Date(Date.now())}
                                    onChange={(e) => setEndDate(new Date(`${e?.toUTCString()}`))}
                                    onError={console.log}
                                    format="yyyy/MM/dd HH:mm"
                                />
                            </MuiPickersUtilsProvider>
                        </ThemeProvider>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogContent>
                {loading ? (
                    <CircularProgress />
                ) : (
                    <div style={{ width: "100%", height: heightCalc }} className="Container">
                        {filteredHistoricalInformation?.data &&
                        filteredHistoricalInformation?.data.length > 0 ? (
                            <AutoSizer onResize={() => cache.current.clearAll()}>
                                {({ width, height }) => {
                                    return (
                                        <div>
                                            <List
                                                width={width}
                                                height={height}
                                                rowHeight={cache.current.rowHeight}
                                                deferredMeasurementCache={cache.current}
                                                rowCount={
                                                    filteredHistoricalInformation?.data.length ?? 0
                                                }
                                                scrollToAlignment="auto"
                                                rowRenderer={({ key, index, style, parent }) => {
                                                    const advertise =
                                                        filteredHistoricalInformation?.data[index];
                                                    return (
                                                        <CellMeasurer
                                                            key={key}
                                                            cache={cache.current}
                                                            parent={parent}
                                                            columnIndex={0}
                                                            rowIndex={index}
                                                        >
                                                            <div style={style}>
                                                                <ListItem
                                                                    key={advertise?.ids}
                                                                    divider
                                                                >
                                                                    <Grid container>
                                                                        <Grid
                                                                            item
                                                                            xs={11}
                                                                            style={{
                                                                                display: "flex",
                                                                                alignItems:
                                                                                    "center",
                                                                            }}
                                                                        >
                                                                            <ListItemIcon
                                                                                style={{
                                                                                    cursor: "pointer",
                                                                                }}
                                                                                onClick={async () => {
                                                                                    await handleReadAdvertise(
                                                                                        `${advertise?.ids}`,
                                                                                        project.id
                                                                                    );
                                                                                    await getAdvertiseHistoryList();
                                                                                }}
                                                                            >
                                                                                {advertise?.read
                                                                                    ? historicalInformation?.iconUnavailable
                                                                                    : historicalInformation?.iconAvailable}
                                                                            </ListItemIcon>
                                                                            <ListItemText
                                                                                primary={
                                                                                    advertise?.title
                                                                                }
                                                                                secondary={
                                                                                    <div
                                                                                        style={{
                                                                                            display:
                                                                                                "flex",
                                                                                            flexDirection:
                                                                                                "row",
                                                                                            justifyContent:
                                                                                                "space-between",
                                                                                        }}
                                                                                    >
                                                                                        <div>
                                                                                            {
                                                                                                advertise?.description
                                                                                            }
                                                                                        </div>
                                                                                        <div
                                                                                            style={{
                                                                                                display:
                                                                                                    "block",
                                                                                                textAlign:
                                                                                                    "right",
                                                                                                WebkitTextStroke:
                                                                                                    "thin",
                                                                                            }}
                                                                                        >
                                                                                            {convertDateIntoString(
                                                                                                advertise?.created
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                }
                                                                            />
                                                                        </Grid>
                                                                        <Grid
                                                                            item
                                                                            xs={1}
                                                                            style={{
                                                                                display: "flex",
                                                                                alignItems:
                                                                                    "center",
                                                                                justifyContent:
                                                                                    "end",
                                                                            }}
                                                                        >
                                                                            {type === "alarms" && (
                                                                                <Tooltip
                                                                                    title={
                                                                                        advertise.values &&
                                                                                        advertise
                                                                                            .values
                                                                                            .length >
                                                                                            0 ? (
                                                                                            advertise.values.map(
                                                                                                (
                                                                                                    x,
                                                                                                    i
                                                                                                ) => {
                                                                                                    const ind =
                                                                                                        i;
                                                                                                    return (
                                                                                                        <div
                                                                                                            key={
                                                                                                                ind
                                                                                                            }
                                                                                                        >
                                                                                                            {
                                                                                                                x
                                                                                                            }
                                                                                                        </div>
                                                                                                    );
                                                                                                }
                                                                                            )
                                                                                        ) : (
                                                                                            <b>
                                                                                                {t(
                                                                                                    "dashboard.cards.noInformation"
                                                                                                )}
                                                                                            </b>
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <Info />
                                                                                </Tooltip>
                                                                            )}
                                                                        </Grid>
                                                                    </Grid>
                                                                </ListItem>
                                                            </div>
                                                        </CellMeasurer>
                                                    );
                                                }}
                                            />
                                        </div>
                                    );
                                }}
                            </AutoSizer>
                        ) : (
                            <>{t("dashboard.cards.noAlarms")}</>
                        )}
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={closeDialog} color="primary" variant="contained">
                    {t("dashboard.cards.close")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default HistoricalAdvertises;
